import React, { useEffect, useState, useCallback } from "react";

interface FeedItem {
  id: string;
  title: string;
  link: string;
  externalLink: string | null;
  description: string;
  thumbnail: string | null;
  author: string;
  pubDate: string;
  subreddit: string;
}

const SUB_COLORS: Record<string, { border: string; bg: string; text: string; dot: string }> = {
  "r/diytubes":     { border: "#e8c070", bg: "#18130a", text: "#e8c070", dot: "#e8c070" },
  "r/ToobAmps":     { border: "#f87171", bg: "#180a0a", text: "#f87171", dot: "#f87171" },
  "r/VintageAudio": { border: "#7bb8e8", bg: "#0a0f18", text: "#7bb8e8", dot: "#7bb8e8" },
};
const DEFAULT_C = { border: "#555", bg: "#111", text: "#888", dot: "#555" };
function getColor(sub: string) { return SUB_COLORS[sub] ?? DEFAULT_C; }

function fmtDate(iso: string) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

function stripHtml(html: string) {
  return html.replace(/<[^>]*>/g, " ").replace(/\s{2,}/g, " ").trim();
}

// ── Modal ──────────────────────────────────────────────────────────────────────
function ArticleModal({ item, onClose }: { item: FeedItem; onClose: () => void }) {
  const c = getColor(item.subreddit);
  const plainText = stripHtml(item.description);
  const hasBody = plainText.length > 30;
  const externalLink = item.externalLink;

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9500,
        background: "rgba(0,0,0,0.9)", backdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#111", border: `1px solid ${c.border}44`,
          borderRadius: 18, width: "100%", maxWidth: 700,
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          overflow: "hidden", boxShadow: "0 32px 80px rgba(0,0,0,0.9)",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 14px", borderBottom: "1px solid #1e1e1e" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                <span style={{
                  background: c.bg, color: c.text, border: `1px solid ${c.border}55`,
                  borderRadius: 12, padding: "3px 10px", fontSize: 12, fontWeight: 700,
                }}>
                  {item.subreddit}
                </span>
                {item.author && (
                  <span style={{ fontSize: 12, color: "#555" }}>
                    u/<span style={{ color: "#888" }}>{item.author}</span>
                  </span>
                )}
                {item.pubDate && (
                  <time dateTime={item.pubDate} style={{ fontSize: 12, color: "#444" }}>{fmtDate(item.pubDate)}</time>
                )}
              </div>
              <h2 style={{ fontSize: 17, fontWeight: 800, color: "#fff", margin: 0, lineHeight: 1.4 }}>
                {item.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: 22, padding: 4, flexShrink: 0, lineHeight: 1 }}
              aria-label="Close"
            >✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          {item.thumbnail && (
            <img
              src={item.thumbnail}
              alt={`${item.title} — article image`}
              style={{ maxWidth: "100%", maxHeight: 240, objectFit: "cover", borderRadius: 10, marginBottom: 16, display: "block" }}
              onError={e => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          )}
          {hasBody ? (
            <div
              className="article-body"
              style={{ fontSize: 14, color: "#c0c0c0", lineHeight: 1.75, whiteSpace: "pre-wrap" }}
            >
              {plainText}
            </div>
          ) : (
            <div style={{ textAlign: "center", padding: "32px 0" }}>
              <div style={{ fontSize: 40, marginBottom: 10 }}>🔗</div>
              <p style={{ fontSize: 14, color: "#555", margin: 0 }}>
                This is a link post. Use the button below to read the linked article.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #1e1e1e", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <a href={item.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            <button style={{
              padding: "9px 18px", background: c.bg, color: c.text,
              border: `1px solid ${c.border}66`, borderRadius: 9,
              fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}>
              View Thread on Reddit ↗
            </button>
          </a>
          {externalLink && (
            <a href={externalLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
              <button style={{
                padding: "9px 18px", background: "#1a1a1a", color: "#e0e0e0",
                border: "1px solid #3a3a3a", borderRadius: 9,
                fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
              }}>
                Open Linked Article ↗
              </button>
            </a>
          )}
          <button
            onClick={onClose}
            style={{
              marginLeft: "auto", padding: "9px 18px", background: "transparent",
              color: "#555", border: "1px solid #2a2a2a", borderRadius: 9,
              fontWeight: 500, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Close
          </button>
        </div>
      </div>

    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────────
function ArticleCard({ item, onClick }: { item: FeedItem; onClick: () => void }) {
  const c = getColor(item.subreddit);
  const [hovered, setHovered] = useState(false);
  const snippet = stripHtml(item.description).slice(0, 140);
  const hasExternal = !!item.externalLink;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? c.bg : "#0d0d0d",
        border: `1px solid ${hovered ? c.border + "88" : "#1e1e1e"}`,
        borderRadius: 12, padding: "14px 16px", cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 8,
        transition: "background 0.15s, border-color 0.15s",
      }}
    >
      {/* Sub badge + date */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{
          background: c.bg, color: c.text, border: `1px solid ${c.border}55`,
          borderRadius: 10, padding: "2px 8px", fontSize: 12, fontWeight: 700, flexShrink: 0,
        }}>
          {item.subreddit}
        </span>
        <time dateTime={item.pubDate} style={{ fontSize: 12, color: "#3a3a3a", flexShrink: 0 }}>
          {fmtDate(item.pubDate)}
        </time>
      </div>

      {/* Title */}
      <div style={{
        fontSize: 13, fontWeight: 700, color: hovered ? "#fff" : "#e0e0e0",
        lineHeight: 1.4, transition: "color 0.15s",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {item.title}
      </div>

      {/* Snippet */}
      {snippet && (
        <div style={{
          fontSize: 12, color: "#555", lineHeight: 1.5,
          display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
        }}>
          {snippet}{snippet.length >= 140 ? "…" : ""}
        </div>
      )}

      {/* Footer row */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {item.author && (
          <span style={{ fontSize: 11, color: "#3a3a3a" }}>u/{item.author}</span>
        )}
        {hasExternal && (
          <span style={{ fontSize: 10, color: "#3a5a3a", background: "#0a1a0a", border: "1px solid #1a3a1a", borderRadius: 6, padding: "1px 6px" }}>
            🔗 link post
          </span>
        )}
      </div>
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────────────────────────
const ALL_SUBS = ["All", "r/diytubes", "r/ToobAmps", "r/VintageAudio"];

export default function ArticlesPanel() {
  const [items, setItems]       = useState<FeedItem[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);
  const [selected, setSelected] = useState<FeedItem | null>(null);
  const [filter, setFilter]     = useState("All");

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch("/api/articles/feed");
      if (!r.ok) throw new Error("Feed unavailable");
      const data = await r.json() as FeedItem[];
      if (!Array.isArray(data) || data.length === 0) throw new Error("empty");
      setItems(data);
    } catch {
      setError("Could not load community posts right now — check back soon.");
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = filter === "All" ? items : items.filter(i => i.subreddit === filter);

  return (
    <section style={{ maxWidth: 1100, margin: "0 auto", padding: "8px 20px 40px" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#fff", margin: "0 0 4px" }}>
            📡 Articles of Interest
          </h2>
          <p style={{ fontSize: 12, color: "#555", margin: 0 }}>
            Latest from the vacuum tube community — r/diytubes · r/ToobAmps · r/VintageAudio
          </p>
        </div>
        <button
          onClick={() => { void load(); }}
          style={{
            marginTop: 4, padding: "6px 14px", background: "#111",
            border: "1px solid #2a2a2a", borderRadius: 8, color: "#555",
            fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}
        >
          ↺ Refresh
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
        {ALL_SUBS.map(s => {
          const active = filter === s;
          const c = getColor(s);
          const count = s === "All" ? items.length : items.filter(i => i.subreddit === s).length;
          return (
            <button
              key={s}
              onClick={() => setFilter(s)}
              style={{
                padding: "5px 14px", borderRadius: 20, border: "1px solid",
                borderColor: active ? (s === "All" ? "#888" : c.border) : "#2a2a2a",
                background: active ? (s === "All" ? "#1a1a1a" : c.bg) : "#0d0d0d",
                color: active ? (s === "All" ? "#ccc" : c.text) : "#555",
                fontWeight: active ? 700 : 400, fontSize: 12,
                cursor: "pointer", fontFamily: "inherit",
                display: "flex", alignItems: "center", gap: 6,
                transition: "all 0.15s",
              }}
            >
              {s !== "All" && (
                <span style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: active ? c.dot : "#3a3a3a",
                  display: "inline-block", flexShrink: 0,
                }} />
              )}
              {s}
              <span style={{
                background: active ? "rgba(255,255,255,0.12)" : "#1a1a1a",
                color: active ? "#fff" : "#444",
                borderRadius: 10, padding: "0 6px", fontSize: 10, fontWeight: 700,
              }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* States */}
      {loading && (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#555", fontSize: 14 }}>
          Loading community posts…
        </div>
      )}
      {error && (
        <div style={{ textAlign: "center", padding: "48px 0" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>📡</div>
          <div style={{ color: "#f87171", fontSize: 14, marginBottom: 12 }}>{error}</div>
          <button onClick={() => { void load(); }} style={{
            padding: "8px 20px", background: "#1a1a1a", border: "1px solid #2a2a2a",
            borderRadius: 8, color: "#888", fontSize: 12, cursor: "pointer", fontFamily: "inherit",
          }}>Try Again</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 12,
          }}>
            {filtered.map(item => (
              <ArticleCard key={item.id} item={item} onClick={() => setSelected(item)} />
            ))}
          </div>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "48px 0", color: "#555", fontSize: 14 }}>
              No posts found.
            </div>
          )}
          {items.length > 0 && (
            <div style={{ marginTop: 16, fontSize: 11, color: "#333", textAlign: "center" }}>
              Community posts from Reddit · cached and refreshed every 10 minutes
            </div>
          )}
        </>
      )}

      {selected && <ArticleModal item={selected} onClose={() => setSelected(null)} />}
    </section>
  );
}
